import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getDiscountPersona } from '@/api/clarityLens/salesLens'
import { pushNotification, useAppDispatch } from '@/store'
import { areFilterParamsValid } from '@/utils/filterParams'
import { DiscountPersonaResponseModel } from '@/types/clarityLens/response/salesLens'

export type DiscountPersonaChartData = {
  rawData: number[][]
  seriesNames: string[]
  xAxisNames: string[]
}

export default function useDiscountPersona(
  startDate: string,
  endDate: string,
  branches: string,
  itemGroupName: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemGroupName?.trim()),
    [startDate, endDate, branches, itemGroupName]
  )
  const loading = !isReady || isFetching

  const [discountPersonaData, setDiscountPersonaData] =
    useState<DiscountPersonaResponseModel[]>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const data = await getDiscountPersona(
        branches,
        startDate,
        endDate,
        itemGroupName
      )

      setDiscountPersonaData(data)
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, branches, startDate, endDate, itemGroupName])

  const refetch = useCallback(() => {
    console.log('Refetching discount persona data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  // Transform response data to stacked bar chart format
  const discountPersonaChartData: DiscountPersonaChartData | null =
    useMemo(() => {
      if (!discountPersonaData || discountPersonaData.length === 0) {
        return null
      }

      return convertToStackedBarFormat(discountPersonaData)
    }, [discountPersonaData])

  return {
    discountPersonaData,
    discountPersonaChartData,
    loading,
    refetch,
  }
}

/**
 * Converts DiscountPersonaResponseModel[] to stacked bar chart format
 *
 * Structure:
 * - xAxisNames: All unique discount types across all personas
 * - seriesNames: All persona names
 * - rawData: Array of arrays, where each inner array represents one persona (series)
 *            and contains the amount for each discount type
 */
function convertToStackedBarFormat(
  data: DiscountPersonaResponseModel[]
): DiscountPersonaChartData {
  // Collect all unique discount types across all personas
  const discountTypeSet = new Set<string>()
  data.forEach((persona) => {
    persona.discount_types.forEach((discountType) => {
      discountTypeSet.add(discountType.type)
    })
  })

  const xAxisNames = Array.from(discountTypeSet).sort()
  const seriesNames = data.map((persona) => persona.persona)

  // Create rawData: each row is a persona, each column is a discount type
  const rawData: number[][] = data.map((persona) => {
    // Create a map for quick lookup of discount type amounts
    const discountTypeMap = new Map<string, number>()
    persona.discount_types.forEach((discountType) => {
      discountTypeMap.set(discountType.type, discountType.amount)
    })

    // For each discount type in xAxisNames, get the amount (or 0 if not present)
    return xAxisNames.map((discountType) => {
      return discountTypeMap.get(discountType) || 0
    })
  })

  return {
    rawData,
    seriesNames,
    xAxisNames,
  }
}
